import { ChangeDetectionStrategy, Component, ElementRef, HostBinding, Input, ViewChild, inject } from '@angular/core';
import { NgIf } from '@angular/common';
import { AnimationController, IonAvatar } from '@ionic/angular/standalone';
import { IFileResponse } from '@api/client';
import { buildAvatarSvgDataUrl } from '@utils/initials';
import { createCenterModalAnimations } from '@utils/modal-animations';
import { StateOverlayService } from '@state-routing';
import { getAvatarPreviewUrl, ImagePreviewSize } from '@utils/image-url.utils';
import { getLoadedImageUrl } from '@utils/image-loading.utils';

@Component({
  selector: 'user-avatar',
  templateUrl: './user-avatar.component.html',
  styleUrls: ['./user-avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    IonAvatar,
  ]
})
export class UserAvatarComponent {
  private readonly stateOverlay = inject(StateOverlayService);
  private readonly animationController = inject(AnimationController);

  @ViewChild('avatarImage') private avatarImage?: ElementRef<HTMLImageElement>;

  @Input() public fullName: string = '';
  @Input() public avatarUrl?: string;
  @Input() public avatarFile?: IFileResponse;
  @Input() public previewSize: ImagePreviewSize = ImagePreviewSize.Small;
  @Input() public typeAvatar: 'default' | 'with-light' | 'user-offline' | 'user-online' = 'default'; // not used
  @Input() public sizeLight: 'big' | 'medium' | 'small' | 'none' | 'super-big' = 'small'; // not used
  @Input() public showOriginalByClick: boolean = true; // not used
  @Input() public userId?: number;
  @Input() public nickname?: string;
  @Input() public official: boolean = false;
  @Input() public enableContactCard: boolean = false;
  @Input() public currentDirectChatUserId: number | null = null;

  protected readonly buildAvatarSvgDataUrl = buildAvatarSvgDataUrl;
  protected isImageError: boolean = false;

  public get loadedImageUrl(): string | null {
    return getLoadedImageUrl(this.avatarImage?.nativeElement);
  }

  @HostBinding('class.contact-card-enabled')
  protected get contactCardEnabled(): boolean {
    return this.enableContactCard && (!!this.userId || !!this.nickname);
  }

  protected get avatarImageUrl(): string | null {
    return getAvatarPreviewUrl(this.avatarFile, this.previewSize, this.avatarUrl);
  }

  // get File(): any {
  //   return {
  //     resizes: this.avatarFile.resizes,
  //     type: {id: 3, name: 'image'},
  //     name: this.name,
  //     origin: this.avatarFile.origin
  //   };
  // }

  // showImage(imageSlider, event) {
  //   if(this.showOriginalByClick) {
  //     event.stopImmediatePropagation();
  //     imageSlider.showImages([this.File], 0);
  //   }
  // }

  public async openContactCard(): Promise<void> {
    if (!this.enableContactCard || (!this.userId && !this.nickname)) return;
    if (typeof window === 'undefined') return;

    const { ContactCardComponent } = await import('@ui-components/contact-card/contact-card.component');
    const { enterAnimation, leaveAnimation } = createCenterModalAnimations(this.animationController, {
      fromScale: 0.9,
      duration: 200,
    });
    await this.stateOverlay.openModal({
      component: ContactCardComponent,
      componentProps: {
        userId: this.userId,
        nickname: this.nickname,
        fullName: this.fullName,
        avatarUrl: this.avatarUrl,
        avatarPreviewUrl: this.loadedImageUrl,
        avatarFile: this.avatarFile,
        official: this.official,
        isOnline: this.typeAvatar === 'user-online',
        currentDirectChatUserId: this.currentDirectChatUserId,
      },
      cssClass: 'contact-card-modal',
      showBackdrop: true,
      backdropDismiss: true,
      enterAnimation,
      leaveAnimation,
    });
  }

  protected markImageAsBad(): void {
    this.isImageError = true;
  }

  protected async onAvatarClick(event: Event): Promise<void> {
    if (!this.contactCardEnabled) return;

    event.stopPropagation();
    event.preventDefault();
    await this.openContactCard();
  }
}
